import { ReactNode } from 'react';
import { Cast, Content, ImageFile } from '../../apiService';
import { Link, Row, Text, Badge, Flex, Paper, Box } from '../../styles';
import styled from '@emotion/styled';
import ContentHeaderCover from './ContentHeaderCover';
import ValidFlags from './ValidFlags';
import ContentActions from './ContentActions';
import ContentRatings from '../../ContentRatings';
import AgeRestriction from './AgeRestriction';
import ContentGenres from '../../ContentGenres';
import { __t } from '../../i18n';
import { getEpisodeIdentifier, getVideoFile, inspectFlags } from '../../utils';
import ContentCast from './ContentCast';
import dynamic from 'next/dynamic';
import useConditionalProps from '../../utils/useConditionalProps';
import CountdownTimer from '../../CountdownTimer';

const ContentBookmark = dynamic(() => import('../../ContentBookmark'), { ssr: false });

export type TeaserDataType = {
  videoUrl: string;
  poster: string | ImageFile;
  posterAlt: string;
  video_type: string;
};

export default function ContentHeader({ content, cast }: ContentHeaderProps) {
  const { cover_image, age_restriction, duration, year, categories, flag, conditional_flag, coming_soon, video_files } = content;

  const { isForeignIP } = inspectFlags(conditional_flag);
  const free = useConditionalProps((s) => s.free);

  const loading = useConditionalProps((s) => s.loading);
  const bookmarked = useConditionalProps((s) => s.bookmarked);
  const contentId = useConditionalProps((s) => s.contentId);

  // const seriesId = useConditionalProps((s) => s.seriesId);
  const showtime = useConditionalProps((s) => s.showtime);
  const ticketStatus = useConditionalProps((s) => s.ticket?.status);
  const updateConditionalProps = useConditionalProps((s) => s.fetch);
  const carrierBtnText = useConditionalProps((s) => s.carrierBtnText);
  const ageRestricted = useConditionalProps((s) => s.ageRestricted);
  // const id = seriesId || contentId;
  const actionProps = {
    countdown_date: coming_soon,
    hasTrailer: !!getVideoFile(content, 'trailers'),
    carrierBtnText,
    comingSoonFlag: flag.includes('coming_soon'),
  };

  const teaser = video_files?.teasers?.[0];
  const videoInfo = teaser?.files_info?.video_type === 'hls' ? teaser?.files_info?.hls_versions?.[0] : teaser?.files_info?.mp4_versions?.[0];

  const teaserData: TeaserDataType = {
    videoUrl: videoInfo?.url ?? '',
    posterAlt: videoInfo?.display_title ?? '',
    poster: teaser?.poster?.path ?? cover_image?.path ?? '',
    video_type: teaser?.files_info?.video_type || '',
  };

  return (
    <ContentHeaderCover teaserData={teaserData} overlay={showtime &&
      <ShowTime duration={showtime}
        ticketStatus={ticketStatus}
        onFinishTicketTimer={() => updateConditionalProps()} />}>
      {/* {isNew && <IsNewBadge />} */}
      {free && <IsFreeBadge />}

      <ContentTitle content={content}>
        <ContentBookmark isBookmarked={bookmarked} loading={loading} contentId={contentId} />
      </ContentTitle>

      <ContentRatings {...content} />
      <AgeRestriction age={age_restriction} />

      {!!cast?.length && <ContentCast cast={cast} mt={2} mb={3} />}

      <InfoRow duration={duration} year={year} categories={categories} flag={flag} />
      {!!categories?.length && <ContentGenres categories={categories} mb={{ xs: 0, md: 1 }} />}

      {isForeignIP || ageRestricted ? <ActionsAlert ageRestricted={ageRestricted} /> : <ContentActions {...actionProps} />}
    </ContentHeaderCover>
  );
}

const ShowTime = ({ duration, onFinishTicketTimer, ticketStatus }: { duration: number; onFinishTicketTimer: () => void, ticketStatus?: 'active' | 'not_active' | 'pending' }) => {
  return <ShowTimeContainer direction="column" p={2}>
    <Text variant="h2" mb={1}>
      سینما آنلاین
    </Text>
    <CountdownTimer value={duration} compact title={ticketStatus === 'active' ? __t('cinema-online.remaining-time') : __t('cinema-online.activation-time')} onFinishTimer={() => onFinishTicketTimer()} />
  </ShowTimeContainer>;
};

const ShowTimeContainer = styled(Flex)(({ theme }) => ({
  borderRadius: theme.radius.medium,
  backgroundColor: theme.rgba(theme.palette.background[100][0], 0.6),
}));

const BadgeWrapper = styled.div(() => ({
  height: '2em',
  marginTop: '-2em',
}));

// const IsNewBadge = () => (
//   <BadgeWrapper>
//     <Badge>{__t('common.new')}</Badge>
//   </BadgeWrapper>
// );
const IsFreeBadge = () => (
  <BadgeWrapper>
    <Badge color='success'>{__t('common.free')}</Badge>
  </BadgeWrapper>
);

const TitleWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    maxWidth: '39rem',
  },
}));

const ContentTitle = ({ content, children }: ContentTitleProps) => {
  const { type, series } = content;
  const title = series?.series_title || content.title;

  const englishTitle = series?.series_original_name || content.original_name;

  const originalTitle = (
    <Text variant="body1" as="h2" color="weak" mb={1} mt="-.5em" weight="medium" english>
      {englishTitle}
    </Text>
  );

  if ((type !== 'episode' && type !== 'season_of_series') || !series)
    return (
      <TitleWrapper>
        <Text variant="h1">
          {title}
          {children}
        </Text>
        {originalTitle}
      </TitleWrapper>
    );

  const { short_id, series_slug } = series;
  const { season, episode, sub_title } = content;
  const episodeIdentifier = getEpisodeIdentifier({ season, episode, numeric: true });

  return (
    <TitleWrapper>
      <Text variant="h1">
        <Link href={`/contents/${short_id}/${series_slug}`}>{title}</Link>
        {children}
      </Text>
      {originalTitle}
      <Text variant="body2" as="p" mb={1} weight="medium">
        {episodeIdentifier}
        {sub_title ? <>&nbsp; -&nbsp; {sub_title}</> : ''}
      </Text>
    </TitleWrapper>
  );
};

const ActionsAlert = ({ ageRestricted }) => (
  <Paper color="strong" mt={4} mb={4}>
    <Text align="center">{ageRestricted ? __t('content.age_restricted.alert') : __t('content.foreign.vpn.alert')}</Text>
  </Paper>
);

const InfoRow = ({ duration, year, categories, flag  }: Pick<Content, 'duration' | 'year' | 'categories' | 'flag'>) => {
  const noData = !(duration || year || categories?.length || (flag && flag !== 'none'));
  if (noData) return null;
  return (
    <Row mb={0} gap={0} cols={{ xs: 1, sm: 2, md: 2, lg: 3  }}>
      <ValidFlags flag={flag}  duration={duration} categories={categories} year={year} />
    </Row>
  );
};

interface ContentHeaderProps {
  content: Content;
  cast?: Cast[];
}

interface ContentTitleProps {
  content: Content;
  children?: ReactNode;
}
